import React from "react"

import { motion } from "framer-motion"
import { NotificationManager } from "react-notifications"

import { ArrowDownIcon, ArrowUpIcon } from "@heroicons/react/24/solid"

import { RunMutation } from "src/types"

import { gql, useMutation } from "hooks/urql"

import EditableDisplay from "components/Forms/EditableDisplay"
import RemoveButton from "components/RemoveButton"

import LessonResource from "./LessonResource"
import NewResource from "./NewResource"
import lessonTopicsFragment from "./lessonTopicsFragment"

const LessonTopic = ({ topic, first, last }) => {
  const [, updateLessonTopic] = useMutation(updateLessonTopicMutation) as [
    any,
    RunMutation
  ]
  const [{ fetching: incrementOrderRunning }, incrementOrder] = useMutation(
    incrementOrderMutation
  ) as [{ fetching: boolean }, RunMutation]
  const [{ fetching: decrementOrderRunning }, decrementOrder] = useMutation(
    decrementOrderMutation
  ) as [{ fetching: boolean }, RunMutation]

  const incrementOrderHandler = () =>
    incrementOrder({ id: topic.id }).then(result => {
      if (result.error) NotificationManager.error(result.error.message)
    })

  const decrementOrderHandler = () =>
    decrementOrder({ id: topic.id }).then(result => {
      if (result.error) NotificationManager.error(result.error.message)
    })

  const updateLessonTopicHandler = ({ value }) =>
    updateLessonTopic({ id: topic.id, name: value }).then(result => {
      if (result.error) NotificationManager.error(result.error.message)
    })

  const TopicName = ({ children }) => {
    const [, removeLessonTopic] = useMutation(removeLessonTopicMutation) as [
      any,
      RunMutation
    ]
    const removeLessonTopicHandler = () =>
      removeLessonTopic({ id: topic.id }).then(result => {
        if (result.error) NotificationManager.error(result.error.message)
      })

    return (
      <div className="flex items-center">
        <RemoveButton onClick={removeLessonTopicHandler} />
        <h4 className="d-inline-block m-0">{children}</h4>
      </div>
    )
  }

  return (
    <motion.li
      positionTransition
      className="rounded-lg border border-solid border-gray-300 bg-neutral-50 p-3 shadow-sm"
    >
      <div className="flex items-center justify-between">
        <EditableDisplay
          displayValue={topic.name}
          save={updateLessonTopicHandler}
          tag={TopicName}
          height="auto"
        />
        <div className="flex items-center">
          <button
            className="mx-2 cursor-pointer rounded-md border-none bg-inherit pt-2 text-xl hover:bg-gray-200"
            onClick={incrementOrderHandler}
            disabled={last || incrementOrderRunning}
          >
            <ArrowDownIcon className="h-6 w-6" />
          </button>
          <button
            className="mx-2 cursor-pointer rounded-md border-none bg-inherit pt-2 text-xl hover:bg-gray-200"
            onClick={decrementOrderHandler}
            disabled={first || decrementOrderRunning}
          >
            <ArrowUpIcon className="h-6 w-6" />
          </button>
        </div>
      </div>

      <div className="mt-3 rounded-md border border-solid border-gray-200 bg-white p-3">
        <span className="text-lg font-semibold">Worksheets</span>
        <ul className="mt-2 list-none p-0">
          {topic.lessonResources.map(resource => (
            <LessonResource
              resource={resource}
              key={resource.id}
              first={resource === topic.lessonResources[0]}
              last={
                resource ===
                topic.lessonResources[topic.lessonResources.length - 1]
              }
            />
          ))}
        </ul>
        <div className="flex items-center">
          <NewResource lessonTopicId={topic.id} signedUrl={topic.signedUrl} />
        </div>
      </div>
    </motion.li>
  )
}

const updateLessonTopicMutation = gql`
  mutation UpdateLessonTopicMutation($id: ID!, $name: String!) {
    updateLessonTopic(id: $id, name: $name) {
      lessonTopic {
        id
        name
      }
    }
  }
`

const decrementOrderMutation = gql`
  ${lessonTopicsFragment}
  mutation DecrementLessonTopicOrderMutation($id: ID!) {
    decrementLessonTopicOrder(id: $id) {
      lessonPlan {
        lessonTopics {
          ...LessonTopicsFields
        }
      }
    }
  }
`

const incrementOrderMutation = gql`
  ${lessonTopicsFragment}
  mutation IncrementLessonTopicOrderMutation($id: ID!) {
    incrementLessonTopicOrder(id: $id) {
      lessonPlan {
        lessonTopics {
          ...LessonTopicsFields
        }
      }
    }
  }
`

const removeLessonTopicMutation = gql`
  ${lessonTopicsFragment}
  mutation RemoveLessonTopicMutation($id: ID!) {
    removeLessonTopic(id: $id) {
      lessonPlan {
        lessonTopics {
          ...LessonTopicsFields
        }
      }
    }
  }
`

export default LessonTopic
